import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BookOpen, FilePdf, FileDoc, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const GuidePage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    api.get('/api/docs/my')
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error('Errore caricamento guide'))
      .finally(() => setLoading(false));
  }, []);

  const download = async (key, format) => {
    setBusy(`${key}-${format}`);
    try {
      const r = await api.get(`/api/docs/${key}/download?format=${format}`, { responseType: 'blob' });
      const match = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : `${key}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download avviato');
    } catch {
      toast.error('Errore durante il download');
    } finally { setBusy(''); }
  };

  const openPreview = async (key) => {
    try {
      const r = await api.get(`/api/docs/${key}/preview`);
      setPreview(r.data);
    } catch {
      toast.error('Anteprima non disponibile');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="guide-page">
        <div className="mb-8">
          <h1 className="font-heading font-semibold text-4xl mb-2">Guida all'uso</h1>
          <p className="text-foreground/70">
            Il manuale della piattaforma, sempre aggiornato. Puoi consultarlo online o scaricarlo in PDF o Word.
          </p>
        </div>

        {loading ? (
          <p className="text-foreground/60" data-testid="guide-loading">Caricamento guide…</p>
        ) : items.length === 0 ? (
          <Card className="rounded-2xl p-8 text-center bg-muted/20" data-testid="guide-empty">
            <p className="text-foreground/70">Nessuna guida disponibile al momento.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((d) => (
              <Card key={d.key} className="rounded-2xl p-5" data-testid={`guide-card-${d.key}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <BookOpen size={22} weight="duotone" className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading font-medium text-lg leading-tight">{d.title}</p>
                    <p className="text-sm text-foreground/70 mt-0.5">{d.subtitle}</p>
                    <p className="text-[11px] text-foreground/55 mt-1">
                      Aggiornato il {new Date(d.updated_at).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-3 border-t border-border/60">
                  <Button size="sm" className="rounded-full h-8" disabled={busy === `${d.key}-pdf`}
                    onClick={() => download(d.key, 'pdf')} data-testid={`guide-pdf-${d.key}`}>
                    <FilePdf size={15} className="mr-1.5" /> Scarica PDF
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-8" disabled={busy === `${d.key}-docx`}
                    onClick={() => download(d.key, 'docx')} data-testid={`guide-docx-${d.key}`}>
                    <FileDoc size={15} className="mr-1.5" /> Word
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full h-8"
                    onClick={() => openPreview(d.key)} data-testid={`guide-preview-${d.key}`}>
                    <Eye size={15} className="mr-1.5" /> Leggi online
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="guide-preview-dialog">
          <DialogHeader>
            <DialogTitle>{preview?.title}</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap text-[13px] leading-relaxed font-sans text-foreground/85" data-testid="guide-preview-content">
            {preview?.markdown}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuidePage;
